/* eslint-disable relay/unused-fields */
import React, { useRef, useState } from 'react'
import { useSuspenseFragment } from 'react-relay-offline'
import { csx } from '@vtex/admin-ui'
import { useSwipeable } from 'react-swipeable'
import { graphql } from 'babel-plugin-relay/macro'
import { PAGE_PADDING } from '@/ui/components'

import { ProductImage } from '../../../../core/components'
import { PRODUCT_SKU_GALLERY } from './constants'
import type { ProducSkuImageMobile_ProductSku$key } from './__generated__/ProducSkuImageMobile_ProductSku.graphql'
import { useSelectedSkuLowResolutionImages } from './hooks'
import { ProductSkuHighlightedPromotion } from '../ProductSkuHighlightedPromotion'
import { ProductSkuImagesGallery } from './ProductSkuImagesGallery'

const skuFragment = graphql`
  fragment ProducSkuImageMobile_ProductSku on ProductSku {
    id
    name
    images {
      edges {
        node {
          id
          url(size: 1000)
          urlFullSize: url(size: 2000)
        }
      }
      ...ProductSkuImagesGallery_ProductSkuImageConnection
    }
    ...ProductSkuHighlightedPromotion_ProductSku
  }
`

const setElementTransition = (element: HTMLDivElement, value: string) => {
  element.style.setProperty('transition', value, 'important')
}

export type ProductSkuImageMobileProps = {
  productSkuRef: ProducSkuImageMobile_ProductSku$key
}

export const ProductSkuImageMobile = ({
  productSkuRef,
}: ProductSkuImageMobileProps) => {
  const productSku = useSuspenseFragment(skuFragment, productSkuRef)
  const swipeableElemRef = useRef<HTMLDivElement>()
  const [shouldShowGallery, setShouldShowGallery] = useState(false)
  const [activeSkuImageIndex, setActiveSkuImageIndex] = useState(0)
  const [selectedSkuLowResolutionImage] = useSelectedSkuLowResolutionImages()

  const getImageOffset = (
    nextActiveSkuImageIndex: number = activeSkuImageIndex
  ) => {
    const gap = PRODUCT_SKU_GALLERY.GAP * nextActiveSkuImageIndex

    const paddingLeft =
      PRODUCT_SKU_GALLERY.PADDING_LEFT * nextActiveSkuImageIndex

    return paddingLeft - gap
  }

  const swipImage = (direction: 'prev' | 'next') => {
    if (!swipeableElemRef.current) return

    setElementTransition(swipeableElemRef.current, 'transform 0.2s')

    const skuImagesCount = productSku.images.edges.length ?? 0

    const nextActiveSkuImageIndex =
      direction === 'next'
        ? Math.min(activeSkuImageIndex + 1, skuImagesCount - 1)
        : Math.max(activeSkuImageIndex - 1, 0)

    const offset = getImageOffset(nextActiveSkuImageIndex)

    const translateValue = `calc(-${
      100 * nextActiveSkuImageIndex
    }% + ${offset}px)`

    swipeableElemRef.current.style.transform = `translateX(${translateValue})`

    setActiveSkuImageIndex(nextActiveSkuImageIndex)
  }

  const { ref, ...swipeableProps } = useSwipeable({
    onSwiping: (data) => {
      if (!swipeableElemRef.current) return

      setElementTransition(swipeableElemRef.current, 'none')

      const translateInitialOffset = `${100 * activeSkuImageIndex}%`
      const offset = getImageOffset()
      const translateValue = `calc(-${translateInitialOffset} + ${data.deltaX}px + ${offset}px)`

      swipeableElemRef.current.style.transform = `translateX(${translateValue})`
    },
    onSwiped: (data) => {
      swipImage(data.dir === 'Left' ? 'next' : 'prev')
    },
    trackMouse: true,
    trackTouch: true,
    delta: {
      up: 1000,
      down: 1000,
    },
  })

  const handleProductSkuImageClick = () => {
    setShouldShowGallery(true)
  }

  const handleProductSkuImagesGalleryNavClick = (
    direction: 'prev' | 'next'
  ) => () => {
    swipImage(direction)
  }

  return (
    <div
      className={csx({
        position: 'relative',
        overflow: 'hidden',
        paddingLeft: `${PAGE_PADDING.MOBILE}px`,
        marginLeft: `-${PAGE_PADDING.MOBILE}px`,
        width: `calc(100% + ${PAGE_PADDING.MOBILE * 2}px)`,
        '@tablet': {
          paddingLeft: `${PAGE_PADDING.TABLET}px`,
          marginLeft: `-${PAGE_PADDING.TABLET}px`,
          width: `calc(100% + ${PAGE_PADDING.TABLET * 2}px)`,
        },
      })}
    >
      <ProductSkuHighlightedPromotion productSkuRef={productSku} />
      <div
        className={csx({
          display: 'grid',
          gridGap: `${PRODUCT_SKU_GALLERY.GAP}px`,
          gridAutoFlow: 'column',
          gridAutoColumns: `calc(100% - ${PRODUCT_SKU_GALLERY.PADDING_LEFT}px)`,
        })}
        ref={(node) => {
          if (!node) return

          ref(node)
          swipeableElemRef.current = node
        }}
        {...swipeableProps}
      >
        {productSku.images.edges.map((skuImage, index) => (
          <ProductImage
            key={skuImage.node.id}
            src={skuImage.node.urlFullSize}
            alt={productSku.name}
            lowResolutionSrc={index === 0 ? selectedSkuLowResolutionImage : ''}
            onClick={handleProductSkuImageClick}
          />
        ))}
      </div>
      {shouldShowGallery && (
        <ProductSkuImagesGallery
          activeSkuImage={productSku.images.edges[activeSkuImageIndex]?.node}
          productSkuImageConnectionRef={productSku.images}
          onCloseGallery={() => setShouldShowGallery(false)}
          onNextImageClick={handleProductSkuImagesGalleryNavClick('next')}
          onPreviousImageClick={handleProductSkuImagesGalleryNavClick('prev')}
        />
      )}
    </div>
  )
}
